import { ChangeDetectorRef, Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { AdminPanelService } from '../../services/admin-panel.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { SortEvent } from 'primeng/api';
import { Table } from 'primeng/table';
import { HttpClientModule } from '@angular/common/http';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { FieldsetModule } from 'primeng/fieldset';
import { AccordionModule } from 'primeng/accordion';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SkeletonModule } from 'primeng/skeleton';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { UtilsService, MessageType } from 'src/app/common';
import { Subject, takeUntil } from 'rxjs';
import { AdminOrganization, AdminOrganizationMember } from '../../config';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  subscriptionStatus: string;
  planType: string;
  subscriptionEndDate: string;
  subscriptionId?: string;
  billingId?: string;
  loading?: boolean;
  details?: boolean;
  billingDetails?: any;
}

@Component({
    selector: 'app-users',
    templateUrl: './users.component.html',
    standalone: true,
    imports: [
        TableModule,
        InputTextModule,
        MultiSelectModule,
        DropdownModule,
        HttpClientModule,
        CommonModule,
        FormsModule,
        InputTextModule,
        ButtonModule,
        CardModule,
        FieldsetModule,
        AccordionModule,
        ProgressSpinnerModule,
        SkeletonModule,
        DialogModule,
        TooltipModule,
    ]
})
export class UsersComponent implements OnInit, OnDestroy {
  loading: boolean = false;
  public users: any[] = [];
  public totalRecords: number = 0;
  public pageSize: number = 10;
  public expandedRows: { [key: string]: boolean } = {};
  @ViewChild('dt') table: Table;

  // Hybrid pagination cache
  public cachedUsers: any[] = [];
  public currentPage = 1; // 1-based
  /** Guard to ignore onPage events caused by internal programmatic paginator reset */
  private internalPageAdjust = false;

  // Delete dialog state
  showDeleteDialog = false;
  userPendingDelete: any = null;
  // Removed ngModel: use internal value store
  deleteInputValue = '';
  public deletionReason = '';
  deleting = false;
  public billingOwnerOrganizations: AdminOrganization[] = [];
  public replacementMembers: AdminOrganizationMember[] = [];
  public selectedReplacementUserId = '';
  public loadingReassignment = false;
  private pendingDeletionOperationId = '';

  // Search debounce timer
  private debounceTimer: any = null;
  public searchValue = '';
  private sortField: string | undefined;
  private sortDescending = false;

  // Subscription management
  private destroy$ = new Subject<void>();

  // Track timeouts for cleanup
  private timeouts: number[] = [];

  constructor(
    private adminService: AdminPanelService,
    private changeDetectorRef: ChangeDetectorRef,
    private utils: UtilsService
  ) { }

  ngOnInit() {
    this.loadPage(1, this.pageSize, true);
    // Subscribe to total count updates
    this.adminService.usersTotalCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(tc => {
        if (tc) this.totalRecords = tc;
      });
  }

  ngOnDestroy() {
    // Clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    // Clear all pending timeouts
    this.timeouts.forEach(id => clearTimeout(id));
    this.timeouts = [];

    // Clear cached data
    this.cachedUsers = [];
    this.users = [];
    this.expandedRows = {};
  }

  openDeleteModal(user: any) {
    this.userPendingDelete = user;
    this.deleteInputValue = '';
    this.deletionReason = '';
    this.resetReassignmentState();
    this.showDeleteDialog = true;
  }

  closeDeleteDialog() {
    if (this.deleting) return;
    this.showDeleteDialog = false;
    this.userPendingDelete = null;
    this.deleteInputValue = '';
    this.deletionReason = '';
    this.resetReassignmentState();
  }

  focusDeleteInput() {
    const timeoutId = window.setTimeout(() => {
      const el = document.getElementById('deleteConfirmInput');
      (el as HTMLInputElement | null)?.focus();
    }, 30);
    this.timeouts.push(timeoutId);
  }

  canConfirmDelete(): boolean {
  return this.deleteInputValue === 'Delete'
    && this.deletionReason.trim().length >= 3
    && !!this.userPendingDelete
    && !this.deleting;
  }

  async confirmDelete() {
    if (!this.canConfirmDelete()) return;
    this.deleting = true;
    let keepDialogOpen = false;
    try {
      const user = this.userPendingDelete;
      if (!user) {
        this.utils.errorMessage(MessageType.TOAST, 'Delete Failed', 'User data missing.');
        return;
      }

      // Use smart delete method that checks subscription status
      const res = await this.adminService.deleteUser(user, this.deletionReason, { handleError: true });

      if (res.success) {
        this.users = this.users.filter(u => u.id !== user.id);
        this.cachedUsers = this.cachedUsers.filter(u => u.id !== user.id);
        this.totalRecords = Math.max(this.totalRecords - 1, 0);

        const deletionSource = res.data?.source === 'billing-and-identity'
          ? 'Stripe and the local database'
          : res.data?.source === 'identity-fallback'
            ? 'the local database after billing cleanup failed'
            : 'the local database';
        const retentionDetail = res.data?.identity.outcome === 'soft-deleted-orders-retained'
          ? ' Historical orders were retained.'
          : '';
        const reconciliationDetail = res.data?.reconciliationStatus === 'retry-pending'
          ? ` Automatic reconciliation is scheduled. Reference: ${res.data.operationId}.`
          : '';
        this.utils.successMessage(
          MessageType.TOAST,
          'User Deleted',
          `User ${user.id} removed from ${deletionSource}.${retentionDetail}${reconciliationDetail}`
        );
      } else if (res.data?.identity.outcome === 'billing-owner-reassignment-required') {
        this.pendingDeletionOperationId = res.data.operationId;
        keepDialogOpen = await this.loadBillingOwnerRemediation(user.id);
      }
    } catch (e) {
    //   console.error('Delete failed', e);
    //   this.utils.errorMessage(MessageType.TOAST, 'Delete Error', 'Unexpected error deleting user.');
      this.utils.handleApiError(e, 'Failed to delete user');
    } finally {
      this.deleting = false;
      if (!keepDialogOpen) {
        this.closeDeleteDialog();
      }
    }
  }

  public async transferBillingOwnershipAndRetry(): Promise<void> {
    const organization = this.billingOwnerOrganizations[0];
    if (!organization?.id || !this.selectedReplacementUserId || this.loadingReassignment) {
      return;
    }

    this.loadingReassignment = true;
    try {
      const response = await this.adminService.transferOrganizationBillingOwner(
        organization.id,
        this.selectedReplacementUserId,
        this.deletionReason,
        { handleError: true }
      );
      if (!response.success) {
        return;
      }

      this.utils.successMessage(
        MessageType.TOAST,
        'Billing Owner Updated',
        `Billing ownership for ${organization.name || `organization #${organization.id}`} was transferred.`
      );
      const userId = this.userPendingDelete?.id;
      if (!userId) {
        return;
      }

      const hasMoreBlockers = await this.loadBillingOwnerRemediation(userId);
      if (!hasMoreBlockers && this.pendingDeletionOperationId) {
        const retry = await this.adminService.retryUserDeletionOperation(
          this.pendingDeletionOperationId,
          `Billing ownership reassigned. ${this.deletionReason}`,
          { handleError: true }
        );
        const identityCompleted = retry.data?.identity.status === 'deleted'
          || retry.data?.identity.status === 'already-absent';
        if (retry.success && identityCompleted) {
          this.users = this.users.filter(user => user.id !== userId);
          this.cachedUsers = this.cachedUsers.filter(user => user.id !== userId);
          this.totalRecords = Math.max(this.totalRecords - 1, 0);
          this.utils.successMessage(
            MessageType.TOAST,
            'User Deleted',
            `Billing ownership was transferred and user ${userId} was deleted.`
          );
          this.loadingReassignment = false;
          this.closeDeleteDialog();
        }
      }
    } finally {
      this.loadingReassignment = false;
    }
  }

  private async loadBillingOwnerRemediation(userId: string): Promise<boolean> {
    const organizationsResponse = await this.adminService.getOrganizations({ force: true, handleError: true });
    this.billingOwnerOrganizations = (organizationsResponse.data ?? []).filter(organization =>
      organization.billingOwnerUserId === userId && !organization.isDeleted
    );
    const organization = this.billingOwnerOrganizations[0];
    if (!organization?.id) {
      this.replacementMembers = [];
      return false;
    }

    const membersResponse = await this.adminService.getOrganizationMembers(organization.id, {
      force: true,
      handleError: true,
      page: 1,
      pageSize: 100
    });
    this.replacementMembers = (membersResponse.data ?? []).filter(member =>
      member.userId !== userId && member.status?.toLowerCase() === 'active'
    );
    this.selectedReplacementUserId = '';
    return true;
  }

  private resetReassignmentState(): void {
    this.billingOwnerOrganizations = [];
    this.replacementMembers = [];
    this.selectedReplacementUserId = '';
    this.loadingReassignment = false;
    this.pendingDeletionOperationId = '';
  }

  onDeleteInput(ev: Event) {
    const val = (ev.target as HTMLInputElement).value || '';
    this.deleteInputValue = val;
  }

  /** Load a page of users; merge into cache */
  private async loadPage(page: number, size: number, force = false) {
    this.loading = true;
    const safePage = page > 0 ? page : 1;
    const safeSize = size > 0 ? size : this.pageSize;
    this.currentPage = safePage;
    this.pageSize = safeSize;
    try {
      const queryOptions: {
        page: number;
        pageSize: number;
        force: boolean;
        q?: string;
        sortField?: string;
        sortDescending?: boolean;
      } = { page: safePage, pageSize: safeSize, force };
      if (this.searchValue.trim()) queryOptions.q = this.searchValue.trim();
      if (this.sortField) {
        queryOptions.sortField = this.sortField;
        queryOptions.sortDescending = this.sortDescending;
      }
      const res = await this.adminService.getPagedUsers(queryOptions);
      if (res.success && res.data?.items) {
        const items = res.data.items;
        this.totalRecords = res.data.totalCount || this.totalRecords;
        // Keep local paginator state in sync with server headers
        if (res.data.pageSize && res.data.pageSize !== this.pageSize) {
          this.pageSize = res.data.pageSize;
        }
        if (res.data.pageNumber && res.data.pageNumber !== this.currentPage) {
          this.currentPage = res.data.pageNumber;
        }
        this.users = items; // direct assignment like orders
        // Update cache after service response
        const timeoutId = window.setTimeout(() => {
          if (safePage === 1 || force) {
            this.cachedUsers = [...items];
          } else {
            const existingIds = new Set(this.cachedUsers.map(u => u.id));
            items.forEach(u => { if (!existingIds.has(u.id)) this.cachedUsers.push(u); });
          }
          if (this.totalRecords < this.cachedUsers.length) this.totalRecords = this.cachedUsers.length;
        }, 0);
        this.timeouts.push(timeoutId);
      }
    } catch (e) {
      console.error('Error loading paged users', e);
    } finally {
      this.loading = false;
    }
  }

  /** Handle paginator event */
  onPage(event: any) {
    if (this.internalPageAdjust) {
      this.internalPageAdjust = false;
      return; // skip handling programmatic change
    }
    const rows = (typeof event?.rows === 'number' && event.rows > 0) ? event.rows : this.pageSize;
    let pageIndex: number | undefined;
    if (typeof event?.page === 'number') pageIndex = event.page; else if (typeof event?.first === 'number' && rows > 0) pageIndex = Math.floor(event.first / rows);
    const requestedPage = (typeof pageIndex === 'number' && pageIndex >= 0) ? pageIndex + 1 : this.currentPage;
    const sizeChanged = rows !== this.pageSize;

    // If page size decreased, just adjust local settings and slice cached data; no API call.
    if (sizeChanged && rows < this.pageSize) {
      this.pageSize = rows;
      this.currentPage = 1; // reset to first page to avoid index overflow
      this.totalRecords = Math.max(this.totalRecords, this.cachedUsers.length);
      this.users = this.cachedUsers.slice(0, rows);
      if (this.table) {
        this.internalPageAdjust = true;
        const timeoutId = window.setTimeout(() => { (this.table as any).first = 0; }, 0);
        this.timeouts.push(timeoutId);
      }
      return;
    }

    // If only navigating pages and data already cached for the requested page range, slice.
    const endIndexNeeded = requestedPage * rows;
    if (!sizeChanged && endIndexNeeded <= this.cachedUsers.length) {
      this.currentPage = requestedPage;
      const start = (requestedPage - 1) * rows;
      this.users = this.cachedUsers.slice(start, start + rows);
      return;
    }

    // If increasing page size and we already have enough cached rows to fill first page, just slice.
    if (sizeChanged && rows > this.pageSize && this.cachedUsers.length >= rows) {
      this.pageSize = rows;
      this.currentPage = 1;
      this.users = this.cachedUsers.slice(0, rows);
      if (this.table) {
        this.internalPageAdjust = true;
        const timeoutId = window.setTimeout(() => { (this.table as any).first = 0; }, 0);
        this.timeouts.push(timeoutId);
      }
      return;
    }

    // Otherwise we need fresh server call (either larger page size than cache or navigating beyond cache)
    if (sizeChanged) {
      // Fetch new first page with new size to keep consistent ordering
      this.loadPage(1, rows);
    } else if (requestedPage !== this.currentPage) {
      this.loadPage(requestedPage, this.pageSize);
    }
  }

  refreshUsers(force = false) {
    this.loadPage(this.currentPage, this.pageSize, force);
  }
  public customSort(event: SortEvent): void {
    this.sortField = event.field;
    this.sortDescending = event.order === -1;
    void this.loadPage(1, this.pageSize, true);
  }

  public applyGlobalFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.table.filterGlobal(filterValue, 'contains');
  }

  onSearchInput(value: string): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.applySearch();
    }, 300);
  }

  private applySearch(): void {
    if (this.table) {
      this.table.first = 0;
    }
    void this.loadPage(1, this.pageSize, true);
  }

  public yesNo(value: boolean): string {
      return value ? 'Yes' : 'No';
    }

    copyToClipboard(text: string, event: Event): void {
      event.stopPropagation();
      navigator.clipboard.writeText(text).then(() => {
        const button = event.target as HTMLElement;
        button.classList.add('copy-success');

        const timeoutId = window.setTimeout(() => {
          button.classList.remove('copy-success');
        }, 1500);
        this.timeouts.push(timeoutId);

        this.utils.successMessage(MessageType.TOAST, 'Copied to clipboard');
      });
    }

    getStatusClass(status: string): string {
      const statusLower = status?.toLowerCase() || '';
      if (statusLower.includes('active')) {
        return 'status-active';
      } else if (statusLower.includes('trial')) {
        return 'status-trial';
      } else if (statusLower.includes('cancel') || statusLower.includes('expired')) {
        return 'status-cancelled';
      }
      return 'status-default';
    }

    toggleRowExpansion(user: User) {
      if (this.expandedRows[user.id]) {
        delete this.expandedRows[user.id];
      } else {
        this.expandedRows[user.id] = true;
        if (!user.details) {
          this.loadUserDetails(user);
        }
      }
    }

    /** trackBy for billing details accordion */
    billingTrackBy(index: number, item: any) {
      return item?.billingId || index;
    }

    async loadUserDetails(user: User) {
        user.loading = true;
        try {
          const billingDetailsResponse = await this.adminService.getBillingDetails(user.billingId);

          // Update the specific user in the array
          const updatedUser = {
            ...user,
            billingDetails: billingDetailsResponse.data || [],
            details: true,
            loading: false
          };

          // Find the index of the user in the array
          const index = this.users.findIndex(u => u.id === user.id);
          if (index !== -1) {
            // Replace the user at the found index
            this.users[index] = updatedUser;
            // Create a new array reference to trigger change detection
            this.users = [...this.users];
          }

          this.utils.logger.info('Updated User:', updatedUser);
          this.changeDetectorRef.detectChanges();
        } catch (error) {
            this.utils.handleApiError(error, 'Failed to load user details')
            user.billingDetails = []; // Set to empty array in case of error
        } finally {
          user.loading = false;
          this.changeDetectorRef.detectChanges();
        }
      }
}
